import React from "react";
import { Flex, Text, Icon, Box } from "@chakra-ui/react";
import {
  FaBoltLightning,
  FaEnvelope,
  FaEnvelopeCircleCheck,
  FaFile,
  FaFileCode,
  FaFileExcel,
  FaFileImport,
  FaUser,
  FaXmark,
} from "react-icons/fa6";
import { Link } from "react-router-dom"; // Import Link for navigation
import {
  color2,
  color3,
  color4,
  color6,
  color7,
  color8,
  greenVivid,
  primary,
  primaryAlt,
  secondary,
  textPrimary,
  textSecondary,
  white,
} from "../../../../../utils/colors";
import dayjs from "dayjs";
import { Trans, useTranslation } from "react-i18next";
import useThemeColors from "../../../../../hooks/useThemeColors";
import { TbFileCheck } from "react-icons/tb";

const ActivityLogItem = ({ log, index }) => {
  const { t } = useTranslation();

  const { whiteMode, backgroundLightMode, textPrimaryMode, newBorderMode } =
    useThemeColors();

  // Status color mapping
  //   const statusColors = {
  //     new: textPrimary,
  //     inactive: color7,
  //     active: color4,
  //     consulting: color1,
  //     planningTreatment: color2,
  //     approvedTreatment: color3,
  //     ongoingTreatment: color4,
  //     cancelledTreatment: color6,
  //     completedTreatment: buttonAltSuccess,
  //   };

  //   const statusTextColors = {
  //     new: textPrimary,
  //     inactive: textPrimary,
  //     active: textPrimary,
  //     consulting: white,
  //     planningTreatment: textPrimary,
  //     approvedTreatment: textPrimary,
  //     ongoingTreatment: textPrimary,
  //     cancelledTreatment: textPrimary,
  //     completedTreatment: textPrimary,
  //   };

  const ACTION_TYPE_STYLES = {
    logTypePatientAccount: {
      bg: textPrimaryMode,
      icon: FaUser,
      iconColor: whiteMode,
    },
    logTypeTreatmentPlan: {
      bg: primaryAlt,
      icon: FaFile,
      iconColor: white,
    },
    logTypeTreatmentPlanApproved: {
      bg: color3,
      icon: FaFile,
      iconColor: white,
    },
    logTypeTreatmentPlanOngoing: {
      bg: color4,
      icon: FaFile,
      iconColor: white,
    },
    logTypeTreatmentPlanPlanning: {
      bg: color2,
      icon: FaFile,
      iconColor: white,
    },
    logTypeTreatmentPlanCompleted: {
      bg: greenVivid,
      icon: FaFile,
      iconColor: textPrimary,
    },
    logTypeTreatmentPlanCancelled: {
      bg: color6,
      icon: FaFile,
      iconColor: white,
    },
    logTypeTreatmentPlanDelete: {
      bg: color6,
      icon: FaFileExcel,
      iconColor: white,
    },
    logTypeSentTreatmentPlan: {
      bg: color8,
      icon: FaEnvelope,
      iconColor: white,
    },
    logTypeTreatmentPlanDuplicate: {
      bg: primaryAlt,
      icon: FaFileCode,
      iconColor: white,
    },
    logTypeDefault: {
      bg: primary,
      icon: FaBoltLightning,
      iconColor: white,
    },
  };

  const { bg, icon, iconColor } =
    ACTION_TYPE_STYLES[log.actionType] || ACTION_TYPE_STYLES["logTypeDefault"];

  // console.log("log", log);
  // console.log("Activity Log Entry:", log);

  return (
    <Flex
      w="100%"
      key={log._id}
      direction="column"
      m="0 0 0rem"
      p="0.75rem 0.5rem"
      bg={whiteMode}
      borderRadius="1rem"
      transition="all 0.2s"
      _hover={{
        bg: backgroundLightMode,
      }}
    >
      <Flex w="100%" align="center" justify="flex-start" h="100%">
        <Flex
          w="2rem"
          direction="column"
          align="center"
          justify="center"
          h="100%"
        >
          <Flex align="center">
            <Flex
              align="center"
              justify="center"
              w="1.25rem"
              h="1.25rem"
              bg={bg}
              border={`0px solid ${newBorderMode}`}
              borderRadius="10rem"
              flexShrink="0"
            >
              {/* ICONS START */}

              <Icon as={icon} color={iconColor} fontSize="0.6rem" />

              {/* ICONS END */}
            </Flex>
          </Flex>
        </Flex>

        <Flex align="center" pl="0.5rem" h="100%">
          <Text
            mr="0.75rem"
            fontSize="xs"
            color={textSecondary}
            fontWeight="500"
          >
            {dayjs(log.createdAt).fromNow()}
          </Text>
        </Flex>
      </Flex>

      <Flex justify="space-between" alignItems="stretch">
        <Flex w="2rem">
          <Flex
            w="2rem"
            direction="column"
            align="center"
            justify="center"
            position="relative"
            h="100%"
          >
            <Box
              position="absolute"
              top="0.5rem"
              bottom="0"
              w="1px"
              bg={newBorderMode}
              left="50%"
              transform="translateX(-50%)"
            />
          </Flex>
        </Flex>

        <Flex w="100%">
          <Flex w="100%" pl="0.5rem" pr="1rem" h="100%" pt="0.25rem">
            <Text
              fontSize="0.8rem"
              fontWeight="500"
              lineHeight="1.6"
              color={textSecondary}
            >
              <Trans
                i18nKey={t(`activityLog.${log.action}`)}
                values={{
                  firstName: (log && log.firstName && log.firstName) || "-",
                  lastName: (log && log.lastName && log.lastName) || "-",
                  performedByName: (log && log.performedByName) || "-",
                  performedByFirstName:
                    (log &&
                      log.performedBy &&
                      log.performedBy.firstName &&
                      log.performedBy.firstName) ||
                    "-",
                  performedByLastName:
                    (log &&
                      log.performedBy &&
                      log.performedBy.lastName &&
                      log.performedBy.lastName) ||
                    "-",
                }}
                components={{
                  name: (
                    <Text as="span" fontWeight="500" color={textPrimary}>
                      {log.firstName.charAt(0)}. {log.lastName}
                    </Text>
                  ),
                  performedBy: (
                    <Link
                      to={`/employees/${
                        log &&
                        log.performedBy &&
                        log.performedBy._id &&
                        log.performedBy._id
                      }`}
                    >
                      <Text
                        as="span"
                        fontWeight="500"
                        color={textPrimaryMode}
                        _hover={{
                          color: primary,
                        }}
                      >
                        {/* asffass */}
                        {log && log.performedByName && log.performedByName}
                        {/* {log &&
                          log.performedBy &&
                          log.performedBy.lastName &&
                          log.performedBy.lastName}{" "}
                        {log &&
                          log.performedBy &&
                          log.performedBy.firstName &&
                          log.performedBy.firstName} */}
                      </Text>
                    </Link>
                  ),
                  treatmentPlan: (
                    <Text
                      as="span"
                      fontWeight="500"
                      color={textPrimaryMode}
                      _hover={{
                        color: primary,
                      }}
                    >
                      {log.treatmentPlanID}
                    </Text>
                  ),
                  newTreatmentPlan: (
                    <Text
                      as="span"
                      fontWeight="500"
                      color={textPrimaryMode}
                      _hover={{
                        color: primary,
                      }}
                    >
                      {log.newTreatmentPlanID}
                    </Text>
                  ),
                  statusOld: (
                    <Text
                      as="span"
                      fontWeight="500"
                      //   p="5px 10px"
                      //   borderRadius="2rem"
                      //   bg={statusColors[log.valueOld]}
                      //   color={statusColors[log.valueOld]}
                      color={textPrimaryMode}
                      _hover={{
                        color: primary,
                      }}
                    >
                      {/* {log.valueOld} */}
                      {t(`patientStatus.${log.valueOld}`)}
                    </Text>
                  ),
                  statusNew: (
                    <Text
                      as="span"
                      fontWeight="500"
                      //   bg={statusColors[log.valueNew]}
                      //   p="5px 10px"
                      //   borderRadius="2rem"
                      //   color={statusColors[log.valueNew]}
                      color={textPrimaryMode}
                      _hover={{
                        color: primary,
                      }}
                    >
                      {/* {log.valueNew} */}
                      {t(`patientStatus.${log.valueNew}`)}
                    </Text>
                  ),
                }}
              />
            </Text>
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
};

export default ActivityLogItem;
